import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../components/Toast';
import { useNavigate } from 'react-router-dom';
import useUrlState from '../../hooks/useUrlState';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { searchMatchAny } from '../../utils/searchMatch';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const emptyForm = { unvan: '', kategori: 'Et', yetkili: '', telefon: '', email: '', adres: '', vade_gun: 30, muhasebe_kodu: '' };

const TedarikcilerPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_SUPPLIERS, user.effectivePermissions);
  const toast = useToast();
  const [tedarikciler, setTedarikçiler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  // Arama URL'e bağlı: cari-hesaplar'a gidip geri dönünce (tarayıcı-geri) korunur.
  const [search, setSearch] = useUrlState('arama', '', { debounceMs: 300 });
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('access_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchTedarikçiler = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/tedarikciler`, { headers });
      if (!res.ok) return;
      const data = await res.json();

      // Tüm tedarikçilerin bakiyelerini tek istekte çek (N+1 → 1)
      const balRes = await fetch(`${API_URL}/api/finance/suppliers/balances`, { headers });
      const balData = balRes.ok ? await balRes.json() : [];
      const balMap = Object.fromEntries(balData.map(b => [b.tedarikci_id, b.guncel_bakiye]));
      const enriched = data.map(t => ({ ...t, bakiye: balMap[t.id] || 0 }));

      setTedarikçiler(enriched);
    } catch (err) {
      logError('Tedarikçi yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTedarikçiler();
  }, [fetchTedarikçiler]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      unvan: t.unvan || '',
      kategori: t.kategori || 'Genel',
      yetkili: t.yetkili || '',
      telefon: t.telefon || '',
      email: t.email || '',
      adres: t.adres || '',
      vade_gun: t.vade_gun ?? 30,
      muhasebe_kodu: t.muhasebe_kodu || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`"${t.unvan}" tedarikçisini silmek istediğinize emin misiniz?\n\nTedarikçi pasife alınacak, faturalar/ödemeler/stok geçmişi korunacak.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/tedarikciler/${t.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        let msg = `"${t.unvan}" pasife alındı.`;
        if (data.acik_fatura_sayisi > 0) {
          msg += `\n\nUyarı: ${data.acik_fatura_sayisi} açık faturası var — cari hesapta görünmeye devam eder.`;
        }
        toast.success(msg);
        invalidateQueries(['tedarikci']);
        fetchTedarikçiler();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Silinemedi');
      }
    } catch {
      toast.error('Bağlantı hatası');
    }
  };

  const handleSave = async () => {
    if (!form.unvan) return toast.warning('Ünvan zorunludur');
    setSaving(true);
    try {
      const url = editingId
        ? `${API_URL}/api/tedarikciler/${editingId}`
        : `${API_URL}/api/tedarikciler`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        invalidateQueries(['tedarikci']);
        setShowModal(false);
        setForm(emptyForm);
        setEditingId(null);
        fetchTedarikçiler();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Hata oluştu');
      }
    } catch (err) {
      toast.error('Bağlantı hatası');
    } finally {
      setSaving(false);
    }
  };

  const filtered = tedarikciler.filter(t =>
    searchMatchAny(t, search, ['unvan', 'yetkili', 'telefon', 'kod', 'muhasebe_kodu'])
  );

  // KPI hesaplamaları
  const toplamBorc = tedarikciler.reduce((sum, t) => sum + (t.bakiye > 0 ? t.bakiye : 0), 0);
  const toplamAlacak = tedarikciler.reduce((sum, t) => sum + (t.bakiye < 0 ? Math.abs(t.bakiye) : 0), 0);
  const borcluSayisi = tedarikciler.filter(t => t.bakiye > 0).length;

  const getBakiyeStyle = (bakiye) => {
    if (bakiye > 0) return { color: '#d32f2f', bg: '#ffebee', text: 'Borçlu' };
    if (bakiye < 0) return { color: '#2e7d32', bg: '#e8f5e9', text: 'Alacaklı' };
    return { color: colors.textSecondary, bg: '#f5f5f5', text: 'Dengede' };
  };

  const inputStyle = {
    width: '100%', padding: '10px', marginBottom: '12px',
    boxSizing: 'border-box', borderRadius: radius.sm, border: `1px solid ${colors.border}`,
    fontSize: '14px'
  };

  if (loading && tedarikciler.length === 0) {
    return (
      <div>
        <Header title="Tedarikçiler" />
        <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Tedarikçiler" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* KPI Kartları */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
          <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '16px', borderLeft: '4px solid #1976d2', boxShadow: shadows.sm }}>
            <div style={{ fontSize: '13px', color: colors.textSecondary }}>Toplam Tedarikçi</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#1976d2' }}>{tedarikciler.length}</div>
          </div>
          <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '16px', borderLeft: '4px solid #d32f2f', boxShadow: shadows.sm }}>
            <div style={{ fontSize: '13px', color: colors.textSecondary }}>Toplam Borç</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#d32f2f' }}>{toplamBorc.toLocaleString('tr-TR')} ₺</div>
            <div style={{ fontSize: '12px', color: colors.textTertiary }}>{borcluSayisi} tedarikçiye</div>
          </div>
          <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '16px', borderLeft: '4px solid #2e7d32', boxShadow: shadows.sm }}>
            <div style={{ fontSize: '13px', color: colors.textSecondary }}>Toplam Alacak</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#2e7d32' }}>{toplamAlacak.toLocaleString('tr-TR')} ₺</div>
          </div>
          <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '16px', borderLeft: '4px solid #7b1fa2', boxShadow: shadows.sm }}>
            <div style={{ fontSize: '13px', color: colors.textSecondary }}>Net Bakiye</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#7b1fa2' }}>{(toplamBorc - toplamAlacak).toLocaleString('tr-TR')} ₺</div>
          </div>
        </div>

        {/* Üst bar: Arama + Butonlar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Tedarikçi ara (ünvan, yetkili, telefon)..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '250px', padding: '10px 14px', borderRadius: radius.sm, border: `1px solid ${colors.border}`, fontSize: '14px' }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => navigate('/finans#cari-hesaplar')}
              style={{ padding: '10px 20px', background: colors.surface, color: '#1565c0', border: '2px solid #1565c0', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap' }}
            >
              Cari Hesaplar
            </button>
            {canManage && (
              <button
                onClick={openNew}
                style={{ padding: '10px 20px', background: styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap' }}
              >
                + Yeni Tedarikçi
              </button>
            )}
          </div>
        </div>

        {/* Bilgi */}
        <div style={{
          background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: radius.md,
          padding: '10px 15px', marginBottom: '16px', fontSize: '13px', color: '#1565c0'
        }}>
          {canManage
            ? 'Satıra tıklayarak tedarikçi bilgilerini düzenleyebilirsiniz. Cari bakiye otomatik hesaplanır (faturalar - ödemeler).'
            : 'Cari bakiye otomatik hesaplanır (faturalar - ödemeler). Tedarikçi ekleme/güncelleme yetkiniz yok.'}
        </div>

        {/* Tablo */}
        <div style={{ background: colors.surface, borderRadius: radius.md, boxShadow: '0 2px 5px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Ünvan</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Hesap Kodu</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Yetkili</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Telefon</th>
                  <th style={{ padding: '8px', textAlign: 'center' }}>Vade (Gün)</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Cari Bakiye</th>
                  <th style={{ padding: '8px', textAlign: 'center' }}>Durum</th>
                  <th style={{ padding: '8px', textAlign: 'center', width: '160px' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => {
                  const bs = getBakiyeStyle(t.bakiye);
                  return (
                    <tr
                      key={t.id}
                      onClick={() => canManage && openEdit(t)}
                      style={{
                        borderBottom: `1px solid ${colors.border}`,
                        cursor: canManage ? 'pointer' : 'default',
                        transition: 'background 0.15s',
                        background: t.bakiye > 0 ? '#fff8f8' : 'transparent'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#e3f2fd'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = t.bakiye > 0 ? '#fff8f8' : 'transparent'; }}
                    >
                      <td style={{ padding: '12px' }}>
                        <strong style={{ borderBottom: '1px dashed #90caf9', paddingBottom: '1px' }}>
                          {t.unvan || '-'}
                        </strong>
                        {t.kod && <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '2px' }}>{t.kod}</div>}
                      </td>
                      <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '13px', color: t.muhasebe_kodu ? '#1565c0' : colors.textTertiary }}>
                        {t.muhasebe_kodu || '-'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ borderBottom: '1px dashed #90caf9', paddingBottom: '1px' }}>
                          {t.yetkili || '-'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ borderBottom: '1px dashed #90caf9', paddingBottom: '1px' }}>
                          {t.telefon || '-'}
                        </span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <span style={{ borderBottom: '1px dashed #90caf9', paddingBottom: '1px' }}>
                          {t.vade_gun ?? 30}
                        </span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: '700', color: bs.color, fontFamily: 'monospace', fontSize: '14px' }}>
                        {t.bakiye !== 0 ? `${Math.abs(t.bakiye).toLocaleString('tr-TR')} ₺` : '-'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {t.bakiye !== 0 ? (
                          <span style={{
                            padding: '4px 10px', borderRadius: radius.lg, fontSize: '12px', fontWeight: 'bold',
                            background: bs.bg, color: bs.color
                          }}>
                            {bs.text}
                          </span>
                        ) : (
                          <span style={{
                            padding: '4px 10px', borderRadius: radius.lg, fontSize: '12px', fontWeight: 'bold',
                            background: t.aktif !== false ? '#d4edda' : '#f8d7da',
                            color: t.aktif !== false ? '#155724' : '#721c24'
                          }}>
                            {t.aktif !== false ? 'Aktif' : 'Pasif'}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          {canManage && (
                            <button
                              onClick={() => openEdit(t)}
                              style={{
                                padding: '6px 12px', borderRadius: radius.sm, border: 'none',
                                background: '#1565c0', color: 'white', cursor: 'pointer',
                                fontSize: '12px', fontWeight: 'bold'
                              }}
                            >
                              Düzenle
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/cari-hesaplar?tedarikci_id=${t.id}`)}
                            style={{
                              padding: '6px 12px', borderRadius: radius.sm, border: '1px solid #1565c0',
                              background: colors.surface, color: '#1565c0', cursor: 'pointer',
                              fontSize: '12px', fontWeight: 'bold'
                            }}
                          >
                            Cari
                          </button>
                          {canManage && (
                            <button
                              onClick={() => handleDelete(t)}
                              style={{
                                padding: '6px 12px', borderRadius: radius.sm, border: 'none',
                                background: '#d32f2f', color: 'white', cursor: 'pointer',
                                fontSize: '12px', fontWeight: 'bold'
                              }}
                            >
                              Sil
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
                      {search ? 'Arama kriterine uygun tedarikçi bulunamadı.' : 'Henüz tedarikçi eklenmemiş.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal: Yeni / Düzenle */}
        {showModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{ background: colors.surface, padding: '30px', borderRadius: radius.lg, width: '500px', maxWidth: '92vw', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
                {editingId ? 'Tedarikçi Düzenle' : 'Yeni Tedarikçi Ekle'}
              </h3>

              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>Ünvan *</label>
              <input style={inputStyle} placeholder="Tedarikçi ünvanı" value={form.unvan} onChange={e => setForm({ ...form, unvan: e.target.value })} />

              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>Kategori</label>
              <select style={inputStyle} value={form.kategori} onChange={e => setForm({ ...form, kategori: e.target.value })}>
                <option value="Et">Et</option>
                <option value="Sakatat">Sakatat</option>
                <option value="Ambalaj">Ambalaj</option>
                <option value="Genel">Genel</option>
              </select>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>Yetkili Kişi</label>
                  <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="Ad Soyad" value={form.yetkili} onChange={e => setForm({ ...form, yetkili: e.target.value })} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>Telefon</label>
                  <input style={{ ...inputStyle, marginBottom: 0 }} placeholder="05XX XXX XX XX" value={form.telefon} onChange={e => setForm({ ...form, telefon: e.target.value })} />
                </div>
              </div>

              <div style={{ height: '12px' }} />

              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>E-posta</label>
              <input style={inputStyle} placeholder="ornek@firma.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />

              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>Adres</label>
              <textarea
                style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
                placeholder="Adres bilgisi"
                value={form.adres}
                onChange={e => setForm({ ...form, adres: e.target.value })}
              />

              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>Vade (Gün)</label>
              <input type="number" min="0" style={{ ...inputStyle }} value={form.vade_gun} onChange={e => setForm({ ...form, vade_gun: parseInt(e.target.value) || 0 })} />

              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>Muhasebe Hesap Kodu</label>
              <input
                style={inputStyle}
                placeholder="Ör: 320.001"
                value={form.muhasebe_kodu}
                onChange={e => setForm({ ...form, muhasebe_kodu: e.target.value })}
              />

              {/* Cari bilgisi (sadece düzenleme modunda) */}
              {editingId && (() => {
                const t = tedarikciler.find(x => x.id === editingId);
                if (!t || t.bakiye === 0) return null;
                const bs = getBakiyeStyle(t.bakiye);
                return (
                  <div style={{
                    background: bs.bg, border: `1px solid ${bs.color}33`, borderRadius: radius.md,
                    padding: '12px 16px', marginBottom: '16px'
                  }}>
                    <div style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '4px' }}>Cari Bakiye</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: bs.color, fontFamily: 'monospace' }}>
                      {Math.abs(t.bakiye).toLocaleString('tr-TR')} ₺
                    </div>
                    <div style={{ fontSize: '12px', color: bs.color, marginTop: '2px' }}>{bs.text}</div>
                    <button
                      onClick={(e) => { e.preventDefault(); navigate(`/cari-hesaplar?tedarikci_id=${t.id}`); }}
                      style={{
                        marginTop: '8px', padding: '6px 14px', borderRadius: radius.sm,
                        border: `1px solid ${bs.color}`, background: colors.surface, color: bs.color,
                        cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'
                      }}
                    >
                      Cari Hesap Detayı
                    </button>
                  </div>
                );
              })()}

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    flex: 1, padding: '12px', border: 'none', borderRadius: radius.sm,
                    background: saving ? '#90caf9' : '#1565c0', color: 'white',
                    cursor: saving ? 'wait' : 'pointer', fontWeight: 'bold', fontSize: '14px'
                  }}
                >
                  {saving ? 'Kaydediliyor...' : (editingId ? 'Güncelle' : 'Kaydet')}
                </button>
                <button
                  onClick={() => { setShowModal(false); setEditingId(null); setForm(emptyForm); }}
                  style={{
                    flex: 1, padding: '12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
                    background: colors.background, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px'
                  }}
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default TedarikcilerPage;
